import { useState, useEffect, useCallback } from "react";
import { ForgotPasswordService } from "@/services/forgot-password.service";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

const RESEND_COOLDOWN = 60;

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const getStoredEmail = (): string => {
  const storedEmail = localStorage.getItem("@email");
  return storedEmail ? JSON.parse(storedEmail) : '';
};

export const useVerifyPassword = () => {
  const { t } = useTranslation("common");
  const { toast } = useToast();

  const [timeLeft, setTimeLeft] = useState(RESEND_COOLDOWN);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const email = getStoredEmail();

  const handleResendEmail = useCallback(async () => {
    if (!email) {
      toast({
        variant: "error",
        description: t("email_not_found"),
      });
      return;
    }

    setIsLoading(true);

    try {
      await ForgotPasswordService.forgotPassword({ email });

      toast({
        variant: "success",
        description: t("email_sent"),
      });

      setIsButtonDisabled(true);
      setTimeLeft(RESEND_COOLDOWN);

      localStorage.setItem('@emailSent', Date.now().toString());

    } catch (error: any) {
      toast({
        variant: "error",
        description: error?.message || t("error_sending_email"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [email, toast, t]);

  useEffect(() => {
    if (!isButtonDisabled) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsButtonDisabled(false);
          return RESEND_COOLDOWN;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isButtonDisabled]);

  useEffect(() => {
    const handleEmailVerification = () => {
      localStorage.setItem('@emailVerified', 'true');

      const redirectUrl = localStorage.getItem('redirectAfterEmailVerification') || '/dashboard';
      localStorage.removeItem('redirectAfterEmailVerification');

      window.location.href = redirectUrl;
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === '@emailVerificationSuccess') {
        handleEmailVerification();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    email,
    timeLeft: formatTime(timeLeft),
    isButtonDisabled: isButtonDisabled || isLoading,
    handleResendEmail,
  };
};
